export const FLOWCODE_MARKETING_BASE_URL = 'https://www.flowcode.com/'
export const getMarketingUrls = (route: string): string => `${FLOWCODE_MARKETING_BASE_URL}${route}`

const MarketingRoutes = [
  'AFFILIATE',
  'CONTACT_SALES',
  'DEVELOPER_PORTAL',
  'ENTERPRISE',
  'FLOWTAG',
  'FREE_QR_CODE_GENERATOR',
  'INDEX',
  'PAGE',
  'PRICING',
  'PRO',
  'PRO_FLEX',
  'PRO_PLUS',
  'TERMS_OF_USE',
  'PRIVACY_POLICY',
  'FLOWCODE_2_SIGN_IN'
] as const
type MarketingRoute = (typeof MarketingRoutes)[number]
type MarketingRoutePageType = {
  [key in MarketingRoute]: string
}

export const MarketingPageRoutes: MarketingRoutePageType = {
  AFFILIATE: getMarketingUrls('affiliate'),
  CONTACT_SALES: getMarketingUrls('contact-us'),
  DEVELOPER_PORTAL: 'https://developer.flowcode.com/documentation/flowcode-api',
  ENTERPRISE: getMarketingUrls('enterprise'),
  FLOWTAG: getMarketingUrls('flowtag'),
  FREE_QR_CODE_GENERATOR: getMarketingUrls('free-qr-code-generator'),
  INDEX: getMarketingUrls(''),
  PAGE: getMarketingUrls('page'),
  PRICING: getMarketingUrls('pricing'),
  PRO: getMarketingUrls('pro'),
  PRO_FLEX: getMarketingUrls('prolus'), // update to proflex when marketing page is ready
  PRO_PLUS: getMarketingUrls('proplus'),
  TERMS_OF_USE: getMarketingUrls('terms-of-use'),
  PRIVACY_POLICY: getMarketingUrls('privacy-policy'),
  FLOWCODE_2_SIGN_IN: 'https://authn.flowcode.com/oauth/account/login'
}

export enum Routes {
  FLOWTAG = '/flowtag',
  HOME = '/home',
  ACCEPT_INVITE = '/accept-invite',
  ACCEPT_ORG_INVITE = '/accept-org-invite/[inviteId]',
  ACCOUNT = '/account',
  ADMIN = '/page/admin',
  ANALYTICS = '/analytics',
  ANALYTICS_CODES = '/analytics/codes',
  ANALYTICS_CONVERSIONS = '/analytics/conversions',
  ANALYTICS_EVENTS = '/analytics/events',
  ANALYTICS_INSIGHTS = '/analytics/insights',
  ANALYTICS_PAGE = '/analytics/pages/[page_id]',
  ANALYTICS_PAGES = '/analytics/pages',
  ARCHIVE_ASSETS = '/archive',
  ASSETS = '/assets',
  AUTO = '/studio/auto',
  BRAND_KIT = '/brand-kit',
  BUNDLE_DETAILS = '/bundles/[suiteId]',
  BUNDLES = '/bundles',
  BUY_PAGE = '/buy',
  BUY_PLAN_PAGE = '/buy/[planType]',
  CODE = '/codes/[batchId]',
  CODE_NOT_FOUND = '/404/code-not-found',
  CODEE = '/autobuilder',
  CODEE_CODE_CREATION = '/codee-code-creation',
  CODEE_LLM = '/codee-llm',
  CODES = '/codes',
  CONFIRM_PASSWORD_RESET = '/confirmresetpassword',
  CREATE_CODE_SUCCESS = '/create-code-success',
  CREATE_FLOWCODE = '/createflowcode',
  CREATE_FLOWCODE_V3 = '/create-flowcode',
  CRM_FORMS = '/forms',
  CRM_FORMS_EDITOR = '/forms/[slug]/editor',
  CRM_FORMS_SUBMISSIONS = '/forms/[slug]/submissions',
  CRM_SUBMISSIONS_PAGE = '/submissions/page/[slug]',
  DASHBOARD = '/dashboard',
  VALIDATE_PRIVACY_REQUEST = '/validate-privacy-request',
  DOMAINS = '/domains',
  EDIT_CODE = '/codes/[batchId]/edit',
  EDIT_STUDIO = '/studio/edit',
  FLOWPAGE = '/page/[slug]',
  FLOWPAGE_CHECKOUT_SUCCESS = '/[slug]/checkoutSuccess',
  FOLDERS = '/folders',
  FORGOT_PASSWORD = '/forgotpassword',
  FREE_QR_CODE_GENERATOR = '/free-qr-code-generator',
  FRONTEGG_SOCIAL_REDIRECT = '/frontegg-social-redirect',
  FRONTEGG_SSO_REDIRECT = '/frontegg-sso-redirect',
  GET_STARTED_FP = 'page/getstarted',
  GSHEETS_ONBOARDING_SUCCESS = '/page/google-onboarding-success',
  IMPERSONATION = '/impersonation',
  INDEX = '/',
  INDEX_TABBED = '/landing/tabbedvariant',
  INTERNAL_TOOLS = '/internal-tools',
  MAINTENANCE = '/maintenance',
  MEMBER = '/org/member',
  MY_ORG_INVITATIONS = '/my-org/invitations',
  MY_ORG_MEMBERS = '/my-org/members',
  MY_ORG_TEAM = '/my-org/teams/[teamId]',
  MY_ORG_TEAMS = '/my-org/teams',
  NOT_FOUND = '/404',
  NOTICE = '/privacy-policy#notice-at-collection', // note: this redirects to webflow
  OPT_OUT = '/optout',
  ORG = '/org',
  ORG_MEMBER = '/org/member',
  PAGE = '/page',
  PAGE_ANALYTICS = '/page/analytics',
  PAGE_EDITOR = '/pages/[id]',
  PAGE_TEMPLATE_EDITOR = '/pages/templates/[id]',
  PAGES = '/pages',
  PAGEV2_RENDERER = '/c/pages/[pageId]',
  PAUSED_ACCOUNT = '/paused-account',
  PAY = '/pay',
  PIXEL_MANAGEMENT = '/analytics/pixel',
  PRIVACY_REQUEST = '/privacyrequest',
  PRIVACY_REQUEST_SUBMITTED = '/privacyrequest/complete',
  REPORT = '/reports/[dashboard_slug]',
  RESET_PASSWORD = '/forgotpassword',
  SEARCH_RESULTS = '/search-results',
  SELECT_APP = '/select-app',
  SELECT_PLAN = '/select-plan',
  SIGN_IN = '/signin',
  SIGN_UP = '/signup',
  STO_GENERIC = '/claim/generic',
  STUDIO = '/studio',
  STUDIO_COMPONENTS = '/studio/components',
  STUDIO_LIBRARY = '/studio/library',
  STUDIO_TEMPLATES = '/studio/templates',
  TEMPLATES_DISCOVER = '/templates/discover',
  TEMPLATES_DISCOVER_CODES = '/templates/discover/codes',
  TEMPLATES_DISCOVER_PAGES = '/templates/discover/pages',
  TEMPLATES_PLACEMENT = '/templates/placement',
  TEMPLATES_YOUR_CODES = '/templates/your/codes',
  TEMPLATES_YOUR_CREATE = '/templates/your/create',
  TEMPLATES_YOUR_PAGES = '/templates/your/pages',
  TEMPLATES_YOUR_TEMPLATES = '/templates/your',
  TRASH = '/trash',
  UPGRADE = '/upgrade',
  VIDEO_TOOL = '/video-qr-code-tool' // That URL does redirect to the video tool from https://github.com/dtx-company/tv-animation
}

export const protectedRoutes = [
  '/page/[slug]',
  '/page/profile',
  '/pages/[id]',
  '/signin',
  '/signup',
  '/sso'
]

export const allowedPausedAccountRoutes = {
  billingAdmin: [Routes.PAUSED_ACCOUNT, Routes.ACCOUNT, Routes.SELECT_PLAN],
  user: [Routes.PAUSED_ACCOUNT],
  pilotPlanBillingAdmin: [Routes.PAUSED_ACCOUNT, Routes.SELECT_PLAN, Routes.BUY_PLAN_PAGE],
  pilotPlanUser: [Routes.PAUSED_ACCOUNT, Routes.SELECT_PLAN]
}
